/**
 * SetAsideModal Component
 * Modal for setting aside (reinstating) an individual power.
 *
 * Flow (per API spec):
 * 1. Modal opens → call validate endpoint + fetch reason codes in parallel
 * 2. If canSetAside=false → show API error message, form disabled
 * 3. If canSetAside=true → show form with reason dropdown
 * 4. User selects reason (+ other reason text if "OT")
 * 5. Review step → confirm details
 * 6. POST to execute endpoint with { reasonCode, otherReason }
 */

import { useState, useEffect, useCallback } from 'react';
import {
  ShieldExclamationIcon,
  ArrowLeftIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { OathModal, OathButton, OathSelect, OathInput, OathSpinner, OathAlert } from '../../../shared/OathUI';
import { toast } from 'react-toastify';
import { setAsidePower, validateSetAside, getSetAsideReasonCodes, SET_ASIDE_REASON_CODES } from '../../../../services/oathPowerActionService';

const SetAsideModal = ({ isOpen, onClose, power, onSuccess }) => {
  const [reasonCode, setReasonCode] = useState('');
  const [otherReason, setOtherReason] = useState('');
  const [step, setStep] = useState('loading'); // 'loading' | 'form' | 'confirm'
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [validation, setValidation] = useState(null); // result from validate endpoint
  const [reasonOptions, setReasonOptions] = useState(
    SET_ASIDE_REASON_CODES.map(r => ({ value: r.code, label: r.description }))
  );

  const powerNo = power?.powerNo;
  const canSetAside = validation?.canSetAside === true;
  const showOtherReason = reasonCode === 'OT';
  const reasonLabel = reasonOptions.find(r => r.value === reasonCode)?.label || '';

  // Validate power and fetch reason codes when modal opens
  useEffect(() => {
    if (!isOpen || !powerNo) return;
    let cancelled = false;

    const init = async () => {
      setStep('loading');
      setError(null);
      setValidation(null);

      const [validationResult, reasonsResult] = await Promise.all([
        validateSetAside(powerNo),
        getSetAsideReasonCodes(),
      ]);

      if (cancelled) return;

      // Set reason codes (fallback is already handled in the service)
      if (reasonsResult.data && Array.isArray(reasonsResult.data)) {
        setReasonOptions(reasonsResult.data.map(r => ({ value: r.code, label: r.description })));
      }

      setValidation(validationResult);

      if (!validationResult.success || !validationResult.canSetAside) {
        setError(validationResult.message || 'This power cannot be set aside.');
      }

      setStep('form');
    };

    init();
    return () => { cancelled = true; };
  }, [isOpen, powerNo]);

  const resetForm = useCallback(() => {
    setReasonCode('');
    setOtherReason('');
    setStep('loading');
    setSubmitting(false);
    setError(null);
    setValidation(null);
  }, []);

  const handleClose = useCallback(() => {
    if (submitting) return;
    resetForm();
    onClose();
  }, [submitting, resetForm, onClose]);

  const validate = () => {
    if (!canSetAside) return validation?.message || 'This power cannot be set aside';
    if (!reasonCode) return 'Reason is required';
    if (showOtherReason && (!otherReason.trim() || otherReason.trim().length < 2)) {
      return 'Other Reason is required (minimum 2 characters)';
    }
    return null;
  };

  const handleReview = () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    setStep('confirm');
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setError(null);

      const data = {
        reasonCode,
        otherReason: showOtherReason ? otherReason.trim() : null,
      };

      const result = await setAsidePower(powerNo, data);

      if (result.success) {
        toast.success(result.message || `Power ${powerNo} was successfully reinstated.`);
        onSuccess?.(result);
        resetForm();
        onClose();
      } else {
        setError(result.message || 'Failed to set aside power');
        setStep('form');
      }
    } catch (err) {
      console.error('Set aside error:', err);
      setError('An unexpected error occurred');
      setStep('form');
    } finally {
      setSubmitting(false);
    }
  };

  const isLoading = step === 'loading';

  return (
    <OathModal
      isOpen={isOpen}
      onClose={submitting ? undefined : handleClose}
      title={`Set-Aside Power ${powerNo || ''}`}
      size="sm"
      footer={
        isLoading ? null : (
          <div className="flex items-center justify-between w-full gap-3">
            {/* Left side */}
            <div>
              {step === 'confirm' && (
                <button
                  onClick={() => setStep('form')}
                  disabled={submitting}
                  className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors disabled:opacity-40"
                >
                  <ArrowLeftIcon className="h-3.5 w-3.5" />
                  Back
                </button>
              )}
            </div>
            {/* Right side */}
            <div className="flex items-center gap-2">
              <OathButton
                variant="outline"
                onClick={handleClose}
                disabled={submitting}
                size="sm"
              >
                Cancel
              </OathButton>
              {step === 'form' ? (
                <OathButton
                  onClick={handleReview}
                  disabled={!canSetAside}
                  size="sm"
                >
                  Review
                </OathButton>
              ) : step === 'confirm' ? (
                <OathButton
                  variant="danger"
                  onClick={handleSubmit}
                  disabled={submitting}
                  size="sm"
                >
                  {submitting ? (
                    <span className="flex items-center gap-2">
                      <OathSpinner size="sm" />
                      Processing...
                    </span>
                  ) : (
                    'Confirm Set-Aside'
                  )}
                </OathButton>
              ) : null}
            </div>
          </div>
        )
      }
    >
      <div className="space-y-4">
        {/* Loading state */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-8 gap-3">
            <OathSpinner size="md" />
            <p className="text-sm text-gray-500 dark:text-gray-400">Validating power...</p>
          </div>
        )}

        {/* Error from validation or submission */}
        {!isLoading && error && (
          <OathAlert variant="error">
            {error}
          </OathAlert>
        )}

        {/* Form step */}
        {step === 'form' && (
          <>
            {/* Validation result banner */}
            {!canSetAside ? (
              <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-700 dark:text-red-300">
                    Cannot set aside this power
                  </p>
                  {validation?.message && (
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                      {validation.message}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <ShieldExclamationIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    This will set aside power <strong>{powerNo}</strong> and reinstate it to <strong>Executed</strong> status.
                  </p>
                  {validation?.defendantName && (
                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                      Defendant: {validation.defendantName}
                      {validation.bondAmount != null && <> &middot; Bond: ${validation.bondAmount.toLocaleString()}</>}
                    </p>
                  )}
                </div>
              </div>
            )}

            <OathSelect
              label="Reason"
              value={reasonCode}
              onChange={setReasonCode}
              options={reasonOptions}
              placeholder="Select a reason..."
              required
              disabled={!canSetAside}
            />

            {showOtherReason && (
              <OathInput
                label="Other Reason"
                value={otherReason}
                onChange={setOtherReason}
                placeholder="Describe the reason..."
                required
                disabled={!canSetAside}
              />
            )}
          </>
        )}

        {/* Confirm step */}
        {step === 'confirm' && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
              <ShieldExclamationIcon className="h-5 w-5 text-orange-600 dark:text-orange-400 flex-shrink-0" />
              <p className="text-sm text-orange-700 dark:text-orange-300">
                Please confirm the set-aside details below.
              </p>
            </div>

            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-3">
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Power Number</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white mt-0.5">{powerNo}</p>
              </div>
              {validation?.defendantName && (
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Defendant</p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white mt-0.5">{validation.defendantName}</p>
                </div>
              )}
              {validation?.bondAmount != null && (
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Bond Amount</p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white mt-0.5">${validation.bondAmount.toLocaleString()}</p>
                </div>
              )}
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Reason</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white mt-0.5">{reasonLabel}</p>
              </div>
              {showOtherReason && otherReason && (
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Other Reason</p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white mt-0.5">{otherReason}</p>
                </div>
              )}
              <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  The set-aside date will be recorded as the current date and time. The power will be reinstated to <strong className="text-gray-700 dark:text-gray-300">Executed</strong> status.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </OathModal>
  );
};

export default SetAsideModal;
